import { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarClock, Info, Loader2, MapPin } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import { Input } from 'components/ui/input';
import { MultiSelect } from 'components/ui/multi-select';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from 'components/ui/drawer';
import { useDebouncedSearch } from 'hooks/useDebouncedSearch';
import { useSpecialties } from 'hooks/useReferentials';
import {
  useCancelInterventionRequest,
  useInterventionCandidates,
  useMissionRequests,
  useSaveInterventionDate,
  useSaveMissionSpecialties,
  useSendInterventionRequest,
} from 'hooks/useInterventionRequests';
import { qualificationFor } from 'constants/qualificationDesks';
import type {
  InterventionCandidate,
  InterventionRequest,
  JiraWriteMeta,
} from 'api/interventionRequestsApi';
import type { TechnicianListItem } from 'api/techniciansApi';
import { technicianFullName } from 'pages/AdminTechnicians/csv';
import { formatDateTime } from 'utils/formatDate';
import { calendarDay, interventionDayRefusal, parisToday } from 'utils/interventionDate';
import { apiErrorMessage } from 'utils/errorMessage';
import CandidateCard from './CandidateCard';
import { SolicitedTechnicians } from './SolicitedTechnicians';
import { HELP, HELP_ALERT } from './helpBlock';
import TechnicianSearchPanel from './TechnicianSearchPanel';
import {
  ASSIGNMENT_DOT,
  ASSIGNMENT_TONE,
  assignmentLabel,
  assignmentState,
  interventionDay,
  nextToTry,
  technicianName,
  type AssignmentState,
  type UpcomingMission,
} from './upcomingAssignment';

/**
 * « Techniciens proposés » — the drawer that sends, and the only place a mission is acted on.
 * Several technicians are ticked and sent in one go since L5b (D45): the first to accept takes
 * the mission, the others are superseded. The subtitle promises no proximity — `distanceKm` is
 * null until L2c ships addresses — and the postal code is typed only where the ticket has none.
 * The intervention date has three states and the desk decides which (D16/D17): with
 * `hasInterventionQualification` off — the shipped default — nothing here writes to Jira.
 *
 * **Nothing here accepts on a technician's behalf**, and nothing removes a Jira participant: the
 * Jira line is read out loud and offers no action, because that list is managed in Jira (D50).
 */

interface AssignTechnicianDrawerProps {
  mission: UpcomingMission | null;
  serviceDeskId: number;
  /**
   * The desk writes the qualification workflow into Jira. Passed down rather than read from
   * `useServiceDesks` here: the screen above already holds the desk list, and a second consumer
   * would be a second place for the default-OFF to be got wrong.
   */
  hasInterventionQualification?: boolean;
  onClose: () => void;
  /**
   * Asks the tab to release this mission: the cancel mutation, its toasts and its Jira report
   * live there, next to the confirmation the removal already went through. Offered on a mission
   * somebody accepted and on no other (R-072) — with nobody retained there is nothing to release.
   */
  onRelease: (mission: UpcomingMission) => void;
  /** `jira` is what the send's Jira leg did, or `null` when the desk has no qualification workflow. */
  onSent: (mission: UpcomingMission, technicianName: string, jira: JiraWriteMeta | null) => void;
}

/** Technicians the operator asked to solicit and has not confirmed yet — nothing leaves until he does. */
interface PendingSend {
  technicianUserIds: number[];
  names: string[];
  allowOutsidePerimeter: boolean;
}

/** One send that did not go out, named: a partial run must never read as a whole one. */
interface SendFailure {
  technicianUserId: number;
  name: string;
  message: string;
}

/** The ticks, and what the run they started did — one state, because they move together. */
interface SelectionState {
  picked: number[];
  sending: boolean;
  sent: number;
  failures: SendFailure[];
}

type SelectionAction =
  | { type: 'toggle'; id: number }
  | { type: 'reset' }
  | { type: 'start' }
  | { type: 'settled'; sent: number; failures: SendFailure[] };

const NO_SELECTION: SelectionState = { picked: [], sending: false, sent: 0, failures: [] };

const selectionReducer = (state: SelectionState, action: SelectionAction): SelectionState => {
  switch (action.type) {
    case 'toggle':
      return {
        ...state,
        picked: state.picked.includes(action.id)
          ? state.picked.filter((id) => id !== action.id)
          : [...state.picked, action.id],
      };
    case 'start':
      return { ...state, sending: true, sent: 0, failures: [] };
    // The ones that went out leave the selection; the ones that did not stay ticked, so a retry
    // is one click rather than a re-tick of a list the operator has already scrolled past.
    case 'settled':
      return {
        picked: action.failures.map((failure) => failure.technicianUserId),
        sending: false,
        sent: action.sent,
        failures: action.failures,
      };
    default:
      return NO_SELECTION;
  }
};

/**
 * What the mission is in, read off **every** request rather than off the newest one: since L5b a
 * mission carries several, and the newest may be a superseded row while another was accepted.
 */
const missionState = (requests: InterventionRequest[]): AssignmentState => {
  if (requests.some((request) => request.status === 'accepted')) return 'accepted';
  if (requests.some((request) => request.status === 'pending')) return 'pending';
  return requests.some((request) => request.status === 'declined') ? 'declined' : 'unassigned';
};

/**
 * A technician already solicited cannot be ticked — the send would come back
 * `INTERVENTION_REQUEST_ALREADY_SOLICITED`. A cancelled request is the one that lifts the state,
 * which is exactly what « Annuler la sollicitation » above the list is for.
 */
const alreadySolicited = (candidate: InterventionCandidate): boolean =>
  candidate.lastRequestStatus !== null && candidate.lastRequestStatus !== 'cancelled';

/** Shared with `TechnicianSearchPanel`, whose hints are the same box in the same drawer. */
/** Rendered with the field it describes, never behind another condition (R-027). */
const POSTAL_REASON_ID = 'assign-postal-code-reason';

const AssignTechnicianDrawer = ({
  mission,
  serviceDeskId,
  hasInterventionQualification = false,
  onClose,
  onRelease,
  onSent,
}: AssignTechnicianDrawerProps) => {
  const { t } = useTranslation();
  const [postalCode, setPostalCode] = useState<string | undefined>(undefined);
  const [postalInput, setPostalInput] = useDebouncedSearch(postalCode, setPostalCode);
  const [skillIds, setSkillIds] = useState<number[]>([]);
  const [correctingCode, setCorrectingCode] = useState(false);
  const [dateInput, setDateInput] = useState('');
  const [confirming, setConfirming] = useState<PendingSend | null>(null);
  /** The pending request the operator asked to withdraw — it cuts a technician's access (D2). */
  const [cancelling, setCancelling] = useState<InterventionRequest | null>(null);
  const [selection, dispatch] = useReducer(selectionReducer, NO_SELECTION);

  const { data: specialties = [] } = useSpecialties();
  const saveSkills = useSaveMissionSpecialties();
  const saveDate = useSaveInterventionDate();
  const send = useSendInterventionRequest();
  const cancel = useCancelInterventionRequest();
  const issueKey = mission?.issueKey ?? null;
  // The ticket's day and never the typed one: a date being corrected and not yet saved must not
  // reshuffle the list under the operator. Null when the ticket carries nothing a day can be read
  // out of — the ranking then filters nobody rather than answering for a day it invented (D60).
  const missionDay = calendarDay(mission?.interventionDate);
  const candidates = useInterventionCandidates(issueKey, serviceDeskId, postalCode ?? '', missionDay);
  const detail = useMissionRequests(issueKey, serviceDeskId);

  // ⚠️ Keyed on the mission only: re-running on a saved date would drop a typed correction (D19).
  useEffect(() => {
    if (!mission) return;
    // Read off the ticket: a mission in Guéret was once searched against Paris, field left empty.
    setPostalCode(mission.storePostalCode ?? undefined);
    setPostalInput(mission.storePostalCode ?? '');
    setCorrectingCode(false);
    setSkillIds(mission.specialties.map((specialty) => specialty.id));
    setConfirming(null);
    setCancelling(null);
    dispatch({ type: 'reset' });
    send.reset();
    cancel.reset();
    saveSkills.reset();
    saveDate.reset();
    // The mutations are fresh objects on every render, and `setPostalInput` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission?.issueKey, mission?.storePostalCode]);

  // Seeded with the ticket's own day, and re-seeded when a save brings a newer one back.
  useEffect(() => {
    setDateInput(calendarDay(mission?.interventionDate) ?? '');
  }, [mission?.issueKey, mission?.interventionDate]);

  if (!mission) return null;

  const page = candidates.data;
  const askedPostalCode = (postalCode ?? '').length >= 2;
  const next = nextToTry(page?.candidates ?? []);
  /**
   * The ticked technicians **still on screen**. Derived and never stored: `selection.picked` is a
   * list of ids that outlives the list it was ticked on, and since L5f a candidate can leave that
   * list without the operator doing anything (the mission's day turned him unavailable). What is
   * counted, what arms the button and what is sent is this one array — the three cannot disagree.
   */
  const picked = (page?.candidates ?? []).filter((candidate) => selection.picked.includes(candidate.id));

  /**
   * The mission's own requests. A cancelled one is not listed: it was withdrawn, and the
   * technician it named is offered again in the list below rather than twice on the screen.
   */
  const requests = (detail.data?.requests ?? []).filter((request) => request.status !== 'cancelled');
  const acceptor = requests.find((request) => request.status === 'accepted') ?? null;
  const pending = requests.filter((request) => request.status === 'pending');
  const declined = requests.filter((request) => request.status === 'declined');
  const superseded = requests.filter((request) => request.status === 'superseded');
  // While the detail is in flight the row's own request is the best thing known — reading
  // « Aucun technicien » for a frame on a mission that is staffed would be a lie with a delay.
  const state = detail.data ? missionState(requests) : assignmentState(mission.request);
  /*
   * Technicians of ours that Jira carries on the ticket with no GPS request behind them (D50).
   * Said, and nothing more: no « solliciter cette personne », no link — the Jira list is managed
   * in Jira, and this line exists so nobody solicits a third person for a ticket already covered.
   */
  const observed = (detail.data?.participants ?? []).filter(
    (participant) => participant.technicianUserId !== null && !participant.solicited
  );

  const fromTicket = !!mission.storePostalCode;
  // A code no prefix resolves is as dead an end as none; `correctingCode` latches the field open.
  const unresolvedCode = fromTicket && !!page && !page.subdivision;
  const askPostalCode = !fromTicket || unresolvedCode || correctingCode;

  // Editable while the ticket sits at the desk's qualification status, and only on a desk that
  // carries the workflow. Everywhere else the date is read-only, with the reason written out.
  const qualifyingStatusId = qualificationFor(serviceDeskId)?.statusId ?? null;
  const dateEditable =
    hasInterventionQualification && qualifyingStatusId !== null && mission.statusId === qualifyingStatusId;
  const interventionDate = interventionDay(mission.interventionDate);
  const dateRefusal = interventionDayRefusal(dateInput);
  // Only Guillaume's workflow needs the date up front (D16). ⚠️ Gated on the flag, default OFF:
  // there the backend requires none either, and field coverage is ~24 % — three missions in four.
  const blockedByDate =
    hasInterventionQualification && !interventionDate && !(dateEditable && dateRefusal === null);
  // No postal code, no department, and the API answers a generic 400 — so the manual search, which
  // exists for exactly those tickets, says so instead of letting the click fail (D19).
  const blockedFromSending = selection.sending || blockedByDate || !askedPostalCode;
  /** Once somebody has accepted, the mission takes no further solicitation — the backend refuses it. */
  const open = state !== 'accepted';

  /**
   * Both send paths land here, and neither sends: the confirmation is where the number of
   * technicians about to be asked is read back, at the moment of the click.
   * `allowOutsidePerimeter` is set by the manual search and by nothing else — a pick made by name
   * *is* the deliberate override, so the guard stays in force on every ranked candidate.
   */
  const askToPropose = (technician: TechnicianListItem, allowOutsidePerimeter = false) => {
    // `!postalCode` also narrows the type — an asserted one reached the API as a generic 400.
    if (blockedFromSending || !postalCode) return;
    setConfirming({
      technicianUserIds: [technician.id],
      names: [technicianFullName(technician) || technician.username],
      allowOutsidePerimeter,
    });
  };

  const askToSendPicked = () => {
    // `picked` and not `selection.picked`: a tick survives a refresh of the list, and since L5f a
    // candidate can leave it (his day turned unavailable). An empty intersection used to confirm
    // zero name, send nothing and still call `onSent` — « sollicitation envoyée » on an empty run.
    if (blockedFromSending || !postalCode || picked.length === 0) return;
    setConfirming({
      technicianUserIds: picked.map((candidate) => candidate.id),
      names: picked.map((candidate) => technicianFullName(candidate) || candidate.username),
      allowOutsidePerimeter: false,
    });
  };

  /**
   * One call per technician: the endpoint sends to one, deliberately (it was not turned into a
   * bulk write). They go out one after the other so a failure names the technician it belongs to
   * — a run where two went out and one did not must say exactly that, never « envoyé ».
   */
  const confirmSend = async () => {
    if (!confirming || blockedFromSending || !postalCode) return;
    const { technicianUserIds, names, allowOutsidePerimeter } = confirming;
    dispatch({ type: 'start' });
    const done: string[] = [];
    const failures: SendFailure[] = [];
    let jira: JiraWriteMeta | null = null;
    for (let index = 0; index < technicianUserIds.length; index += 1) {
      const technicianUserId = technicianUserIds[index];
      try {
        const result = await send.mutateAsync({
          issueKey: mission.issueKey,
          serviceDeskId,
          technicianUserId,
          postalCode,
          allowOutsidePerimeter,
          // Only when the ticket carries none: the server resolves the date it already holds first
          // and would ignore this one, so sending it over an existing date writes nothing.
          ...(dateEditable && !mission.interventionDate && dateRefusal === null
            ? { interventionDate: dateInput }
            : {}),
        });
        done.push(technicianName(result.data));
        // The first Jira answer of the run: the transition happens once, on the first send.
        jira = jira ?? result.jira;
      } catch (error) {
        failures.push({ technicianUserId, name: names[index], message: apiErrorMessage(t, error) });
      }
    }
    dispatch({ type: 'settled', sent: done.length, failures });
    // Closing on a partial run would hide the half that did not go out, so the panel stays open
    // and names it; the dialog stays open too, since that is where the click happened.
    if (failures.length === 0) {
      setConfirming(null);
      onSent(mission, done.join(' · '), jira);
    }
  };

  const confirmCancel = () => {
    if (!cancelling) return;
    cancel.mutate(
      { issueKey: mission.issueKey, serviceDeskId, requestId: cancelling.id },
      { onSuccess: () => setCancelling(null) }
    );
  };

  const saveMissionSkills = () =>
    saveSkills.mutate({ issueKey: mission.issueKey, serviceDeskId, specialtyIds: skillIds });

  // Guarded as well as disabled: the reason is on screen either way, and a past date must never
  // leave the browser only to come back as a 400.
  const saveInterventionDate = () => {
    if (dateRefusal !== null || saveDate.isPending) return;
    saveDate.mutate({ issueKey: mission.issueKey, serviceDeskId, interventionDate: dateInput });
  };

  /** « 3 sollicités · 1 accepté · 2 dépassées » — the segments that have something to count. */
  const recap = [
    t('{{count}} solicited', { count: requests.length }),
    acceptor ? t('{{count}} accepted', { count: 1 }) : null,
    pending.length ? t('{{count}} awaiting an answer', { count: pending.length }) : null,
    declined.length ? t('{{count}} refused', { count: declined.length }) : null,
    superseded.length ? t('{{count}} superseded', { count: superseded.length }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Drawer open direction="right" shouldScaleBackground={false} onOpenChange={(next) => !next && onClose()}>
      {/* Three panes, one scrolling child: vaul stamps `touch-action: none` on `DrawerContent`. */}
      <DrawerContent side="right" className="overflow-hidden">
        {/* The mission is the title, as the mock-up has it: key and state, store, then the job. */}
        <DrawerHeader className="shrink-0 gap-1 text-left">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="font-mono text-[12.5px] font-semibold text-muted-foreground">
              {mission.issueKey}
            </span>
            <span
              className={cn(
                'inline-flex h-[23px] shrink-0 items-center gap-1.5 rounded-full pl-2 pr-[9px] text-xs font-medium',
                ASSIGNMENT_TONE[state]
              )}
            >
              <span className={cn('h-1.5 w-1.5 rounded-full', ASSIGNMENT_DOT[state])} aria-hidden="true" />
              {assignmentLabel(t, state)}
            </span>
          </div>
          <DrawerTitle className="text-base tracking-[-0.01em]">
            {mission.store || mission.issueKey}
          </DrawerTitle>
          <DrawerDescription className="text-[13px]">{mission.summary}</DrawerDescription>
        </DrawerHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-4">

          {/* A read that failed leaves the panel unable to say who was solicited — and an empty
              list would say « personne », which is the one thing it must not claim by accident. */}
          {detail.error && (
            <p className={HELP_ALERT} role="alert">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {apiErrorMessage(t, detail.error)}
            </p>
          )}

          <SolicitedTechnicians
            requests={requests}
            acceptor={acceptor}
            pendingCount={pending.length}
            recap={recap}
            cancelError={cancel.error}
            onCancel={setCancelling}
            onRelease={() => onRelease(mission)}
          />

          {/*
            Above the field it asks to fill, and not under the list three screens down (D59): an
            alert that names a control the reader has already scrolled past is a riddle. `open`
            because a mission somebody accepted is not waiting for a date — and `dateEditable`
            decides which of the two sentences, here or in Jira, tells the truth.
          */}
          {blockedByDate && open && (
            <p className={HELP_ALERT} role="alert">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {dateEditable
                ? t('This ticket carries no intervention date, so nobody can be sent to it yet. Set one below — saving it, or sending, writes it on the ticket.')
                : t('This ticket carries no intervention date, so nobody can be sent to it yet. Set the date in Jira on the ticket, then reopen this panel.')}
            </p>
          )}

          {/* The date first (D16), in three states, and a Save rendered only where it can succeed. */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="assign-intervention-date" className="flex items-center gap-1.5 text-[13px] font-medium">
              {t('Intervention date')}
              {dateEditable && !interventionDate && <span className="text-rose-600 dark:text-rose-400">*</span>}
            </label>
            {dateEditable ? (
              <>
                {/* Same pair as « Compétences requises » below: field, then its own Save. */}
                <div className="flex items-start gap-2">
                  <Input
                    id="assign-intervention-date"
                    type="date"
                    className="flex-1"
                    value={dateInput}
                    // The picker refuses an earlier day; the guard below holds for a typed one.
                    min={parisToday()}
                    onChange={(event) => setDateInput(event.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={saveInterventionDate}
                    disabled={saveDate.isPending || dateRefusal !== null}
                  >
                    {saveDate.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}
                    {t('Save')}
                  </Button>
                </div>
                <p className="text-[12px] text-muted-foreground/80">
                  {t('Written on the ticket in Jira. Saving it alone is fine — the mission stays being qualified until a technician is sent.')}
                </p>
                {/* The reason, before the request goes out — a 400 after a click is a trap. */}
                {dateRefusal === 'empty' && interventionDate && (
                  <p className="text-[12.5px] text-rose-600 dark:text-rose-400" role="alert">
                    {t('Pick a day before saving. Emptying the field here does not clear the date on the ticket.')}
                  </p>
                )}
                {dateRefusal === 'past' && (
                  <p className="text-[12.5px] text-rose-600 dark:text-rose-400" role="alert">
                    {t('An intervention date cannot be in the past. Pick today or a later day.')}
                  </p>
                )}
                {dateRefusal === 'malformed' && (
                  <p className="text-[12.5px] text-rose-600 dark:text-rose-400" role="alert">
                    {t('This is not a real calendar day. Check the day and the month.')}
                  </p>
                )}
                {saveDate.error && (
                  <p className="text-[12.5px] text-rose-600 dark:text-rose-400" role="alert">
                    {apiErrorMessage(t, saveDate.error)}
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="flex items-center gap-1.5 rounded-md bg-muted/50 px-3 py-2 font-mono text-[13px]">
                  <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                  {interventionDate ?? '—'}
                  {interventionDate && (
                    <span className="font-sans text-[11.5px] text-muted-foreground">{t('from the ticket')}</span>
                  )}
                </p>
                <p className="text-[12px] text-muted-foreground">
                  {hasInterventionQualification
                    ? t('The date is locked: this mission is no longer being qualified, so Jira no longer accepts a change from here.')
                    : t('Writing to Jira is not enabled for this service desk. The date comes from the ticket and cannot be changed here, and sending will not change the ticket status: the mission will stay in this tab.')}
                </p>
              </>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="assign-postal-code" className="flex items-center gap-1.5 text-[13px] font-medium">
              {t('Store postal code')}
              {askPostalCode && <span className="text-rose-600 dark:text-rose-400">*</span>}
              {/* On the exception only: a field that is always there teaches the operator nothing. */}
              {!fromTicket && (
                <span className="inline-flex items-center gap-1 text-[11px] font-normal text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  {t('missing from the ticket')}
                </span>
              )}
              {/* Its own cause: empty field, unmapped code and uncovered department differ. */}
              {fromTicket && askPostalCode && (
                <span className="inline-flex items-center gap-1 text-[11px] font-normal text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  {t('unresolved on the ticket')}
                </span>
              )}
            </label>
            {!askPostalCode ? (
              // Read-only: an editable field would invite a correction that goes nowhere.
              <p className="flex items-center gap-1.5 rounded-md bg-muted/50 px-3 py-2 font-mono text-[13px]">
                {mission.storePostalCode}
                <span className="font-sans text-[11.5px] text-muted-foreground">{t('from the ticket')}</span>
              </p>
            ) : (
              <>
                <Input
                  id="assign-postal-code"
                  value={postalInput}
                  // The asterisk is visual only; this is what says "required" out loud.
                  required
                  aria-required="true"
                  aria-describedby={POSTAL_REASON_ID}
                  maxLength={12}
                  autoComplete="off"
                  placeholder={t('e.g. 59000')}
                  onChange={(event) => {
                    setPostalInput(event.target.value);
                    setCorrectingCode(true);
                  }}
                />
                {/* Why the field is here, in words rather than in a `title` nobody can hover. */}
                <p id={POSTAL_REASON_ID} className="text-[12px] leading-relaxed text-amber-700 dark:text-amber-400">
                  {fromTicket
                    ? t('The postal code on this ticket matches no department, so it cannot filter the technicians — a code outside mainland France, or a typo in Jira. Correct it here to see who covers the store.')
                    : t('This ticket carries no store postal code in its Jira data, so it has to be typed. Check it against the store — the department it resolves to is what filters the technicians.')}
                </p>
              </>
            )}
            {/* The department the server resolved, or the fact that none matched — two sentences. */}
            {askedPostalCode && page && (
              // A live region: on the correction path this sentence is the only feedback a reader
              // gets after typing a code, and it carries both outcomes — resolved and unresolved.
              <p role="status" className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                {page.subdivision
                  ? `${page.subdivision.name} (${page.subdivision.code})`
                  : t('No department matches this postal code — check it, or add the prefix to the referential.')}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="assign-skills" className="text-[13px] font-medium">
              {t('Skills required by the mission')}
            </label>
            {/* One chip row reserved: `MultiSelect` grows ~26 px on a first pick, moving the list. */}
            <div className="flex min-h-[64px] items-start gap-2">
              <MultiSelect
                id="assign-skills"
                aria-label={t('Skills required by the mission')}
                className="flex-1"
                options={specialties.map((specialty) => ({ value: String(specialty.id), label: specialty.name }))}
                selected={skillIds.map(String)}
                onChange={(values) => setSkillIds(values.map(Number))}
                placeholder={t('Select…')}
                searchable
              />
              <Button
                type="button"
                variant="outline"
                onClick={saveMissionSkills}
                disabled={saveSkills.isPending}
              >
                {saveSkills.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}
                {t('Save')}
              </Button>
            </div>
            {/* An empty list is legal and clears them: an ops mistake must be undoable. */}
            <p className="text-[12px] text-muted-foreground/80">
              {t('The ranking is computed from these. Saving reorders the list below.')}
            </p>
            {saveSkills.error && (
              <p className="text-[12.5px] text-rose-600 dark:text-rose-400" role="alert">
                {apiErrorMessage(t, saveSkills.error)}
              </p>
            )}
          </div>

          {/* A run that half went out says both halves, and never « envoyé » alone. */}
          {selection.sent > 0 && selection.failures.length > 0 && (
            <p className="text-[12.5px] text-muted-foreground" role="status">
              {t('{{count}} solicitation sent', { count: selection.sent })}
            </p>
          )}
          {selection.failures.map((failure) => (
            <p key={failure.technicianUserId} className={HELP_ALERT} role="alert">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {t('Nothing was sent to {{name}}: {{reason}}', { name: failure.name, reason: failure.message })}
            </p>
          ))}

          {/* Everything below solicits, and a mission somebody accepted takes no more solicitation. */}
          {open && (
            <>
              {/* The sentence stays visible, never a tooltip: FLOW-017 §11 holds it out loud. */}
              <div className="flex flex-col gap-1 border-t pt-[14px]">
                <div className="flex items-center justify-between gap-2.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[.05em] text-muted-foreground/70">
                    {requests.length > 0 ? t('Solicit other technicians') : t('Proposed technicians')}
                  </span>
                  <span className="text-[11.5px] text-muted-foreground/70">
                    {t('Tick one or more technicians')}
                  </span>
                </div>
                <p className="text-[11.5px] leading-relaxed text-muted-foreground/70">
                  {t('Ranked by how much of the mission’s skills each one covers. Proximity is not in the ranking yet — the technician’s address arrives with the logistics lot.')}
                </p>
              </div>

              {/* Observation, no action: the Jira list is managed in Jira, and this is what stops
                  a third person being solicited for a ticket somebody is already on (D50). */}
              {observed.map((participant) => (
                <p key={participant.accountId} className={HELP}>
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400 dark:text-zinc-500" aria-hidden="true" />
                  <span>
                    {participant.addedByName && participant.addedAt
                      ? t('Jira also carries {{name}} on the ticket, added by {{author}} on {{date}}, with no GPS request.', {
                          name: participant.displayName,
                          author: participant.addedByName,
                          date: formatDateTime(participant.addedAt),
                        })
                      : t('Jira also carries {{name}} on the ticket, with no GPS request.', {
                          name: participant.displayName,
                        })}{' '}
                    {t('Information only: the Jira list is managed in Jira.')}
                  </span>
                </p>
              ))}

              {/* Four states, and the mock-up has none of them: it cannot load, fail or come back empty. */}
              {!askedPostalCode ? (
                <p className={HELP}>
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400 dark:text-zinc-500" aria-hidden="true" />
                  {t('Enter the store postal code to see who covers its department.')}
                </p>
              ) : candidates.isLoading ? (
                <div className="flex items-center justify-center py-8" role="status">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
                  <span className="sr-only">{t('Loading…')}</span>
                </div>
              ) : candidates.error ? (
                <p className="text-[13px] text-rose-600 dark:text-rose-400" role="alert">
                  {apiErrorMessage(t, candidates.error)}
                </p>
              ) : page && page.candidates.length === 0 ? (
                <p className={HELP}>
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400 dark:text-zinc-500" aria-hidden="true" />
                  {/* Three causes, three sentences: « nobody covers this department » would be a
                      lie on a department covered by people who are simply away that day (D61). */}
                  {!page.subdivision
                    ? t('No department matches this postal code, so nobody can be proposed.')
                    : page.unavailableCount > 0
                      ? t('Everyone covering this department is unavailable on {{date}}. Search by name to send anyway, or change the intervention date.', {
                          date: interventionDate,
                        })
                      : t('No active technician covers this department. Add the department to a technician’s scope, or check the postal code.')}
                </p>
              ) : (
                /* Capped and scrolled, with the mock-up's fade saying there is more below. */
                <div className="relative">
                  <ul className="grid max-h-[300px] grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-[9px] overflow-y-auto pr-0.5">
                    {page?.candidates.map((candidate) => (
                      <CandidateCard
                        key={candidate.id}
                        candidate={candidate}
                        missionSpecialtyIds={page.missionSpecialties.map((specialty) => specialty.id)}
                        recommended={candidate.id === next?.id}
                        alreadySolicited={alreadySolicited(candidate)}
                        selected={selection.picked.includes(candidate.id)}
                        onToggle={(picked) => dispatch({ type: 'toggle', id: picked.id })}
                      />
                    ))}
                  </ul>
                  {(page?.candidates.length ?? 0) > 3 && (
                    <div
                      className="pointer-events-none absolute inset-x-0 bottom-0 h-[52px] bg-gradient-to-t from-background from-[12%] to-transparent"
                      aria-hidden="true"
                    />
                  )}
                </div>
              )}

              {/* A list silently shortened is a technician one looks for and does not find. */}
              {(page?.unavailableCount ?? 0) > 0 && (page?.candidates.length ?? 0) > 0 && (
                <p className="text-[11.5px] text-muted-foreground/70">
                  {t('{{count}} technician covering this department is unavailable on {{date}} and is not proposed.', {
                    count: page?.unavailableCount ?? 0,
                    date: interventionDate,
                  })}
                </p>
              )}

              {/* Offered whatever the ranking answered, and keyed on the mission so it inherits no term. */}
              <TechnicianSearchPanel
                key={mission.issueKey}
                subdivision={page?.subdivision ?? null}
                disabled={blockedFromSending}
                noPostalCode={!askedPostalCode}
                // Nobody ranked, for whatever reason: the search is then the only path, so it opens.
                rankedEmpty={!candidates.isLoading && (page?.candidates.length ?? 0) === 0}
                sendingTo={null}
                onSend={(technician) => askToPropose(technician, true)}
              />
            </>
          )}
        </div>

        {/* Outside the scroll region, so it stays reachable whatever the drawer's height. */}
        <div className="flex shrink-0 flex-col gap-2 border-t border-zinc-200 px-5 pb-6 pt-4 dark:border-zinc-800">
          {open && (
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              {picked.length === 0
                ? t('The first to accept takes the mission; the others become « Superseded ».')
                : t('{{count}} technician ticked · the first to accept takes the mission', {
                    count: picked.length,
                  })}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('Close')}
            </Button>
            {open && (
              <Button
                type="button"
                disabled={blockedFromSending || picked.length === 0}
                onClick={askToSendPicked}
              >
                {selection.sending && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                {t('Send {{count}} solicitations', { count: picked.length })}
              </Button>
            )}
          </div>
        </div>

        {/*
          The gate in front of the send — the same call, once per technician. `warning` and not
          `danger`: soliciting somebody destroys nothing, and nothing is replaced any more (D45).
        */}
        <ConfirmDeleteDialog
          open={confirming !== null}
          tone="warning"
          title={t('Send {{count}} solicitations?', { count: confirming?.technicianUserIds.length ?? 1 })}
          description={
            <span className="flex flex-col gap-2">
              <span>
                {t('{{count}} technician is asked straight away, and answers from their own screen.', {
                  count: confirming?.technicianUserIds.length ?? 1,
                })}
              </span>
              {(confirming?.technicianUserIds.length ?? 0) > 1 && (
                <span>{t('The first to accept takes the mission; the others become « Superseded ».')}</span>
              )}
              {confirming?.allowOutsidePerimeter && (
                <span className="text-amber-700 dark:text-amber-400">
                  {t('This technician does not cover the mission’s department. The send is allowed, and recorded as made outside the perimeter.')}
                </span>
              )}
            </span>
          }
          details={
            confirming
              ? [
                  { label: t('Mission'), value: mission.issueKey, emphasis: true },
                  {
                    label: confirming.names.length > 1 ? t('Technicians') : t('Technician'),
                    value: confirming.names.join(' · '),
                    emphasis: true,
                  },
                ]
              : []
          }
          confirmLabel={t('Send the request')}
          loading={selection.sending}
          error={send.error ? apiErrorMessage(t, send.error) : null}
          onConfirm={confirmSend}
          onClose={() => setConfirming(null)}
        />

        {/*
          D2 — the mock-up withdraws a solicitation with no confirmation. It cuts a technician's
          access to a mission he may be on his way to, so it asks first, like every other write
          on this screen.
        */}
        <ConfirmDeleteDialog
          open={cancelling !== null}
          tone="warning"
          title={t('Cancel this solicitation?')}
          description={t('The technician loses access to this mission straight away and is no longer reminded. The other solicitations are untouched, and nothing is sent to anybody else.')}
          details={
            cancelling
              ? [
                  { label: t('Mission'), value: mission.issueKey, emphasis: true },
                  { label: t('Technician'), value: technicianName(cancelling), emphasis: true },
                ]
              : []
          }
          confirmLabel={t('Cancel the solicitation')}
          // « Annuler » beside « Annuler la sollicitation » would be two buttons saying the
          // opposite with the same word.
          cancelLabel={t('Back')}
          loading={cancel.isPending}
          error={cancel.error ? apiErrorMessage(t, cancel.error) : null}
          onConfirm={confirmCancel}
          onClose={() => {
            setCancelling(null);
            cancel.reset();
          }}
        />
      </DrawerContent>
    </Drawer>
  );
};

export default AssignTechnicianDrawer;
